// hooks/useArtworkNavigation.ts
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ArtworkStub, ArtworkNavigationState } from '../models/Artwork';

interface UseArtworkNavigationProps {
    artworks: ArtworkStub[];
    currentArtworkId: number;
}

export const useArtworkNavigation = ({
    artworks,
    currentArtworkId
}: UseArtworkNavigationProps) => {
    const navigate = useNavigate();

    // Find current position in the array
    const currentIndex = useMemo(() => {
        return artworks.findIndex(a => a.artworkId === currentArtworkId);
    }, [artworks, currentArtworkId]);

    // Navigation logic
    const navigateToArtwork = useCallback((artworkId: number, source: ArtworkNavigationState['source'] = 'gallery') => {
        navigate(`/artwork/${artworkId}`, {
            state: {
                source,
                scrollPosition: window.scrollY
            } as ArtworkNavigationState
        });
    }, [navigate]);

    const navigateRelative = useCallback((direction: 'prev' | 'next') => {
        const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

        if (newIndex >= 0 && newIndex < artworks.length) {
            const targetArtwork = artworks[newIndex];
            navigateToArtwork(targetArtwork.artworkId);
        }
    }, [currentIndex, artworks, navigateToArtwork]);

    // Keyboard navigation
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'ArrowLeft') {
            navigateRelative('prev');
        } else if (e.key === 'ArrowRight') {
            navigateRelative('next');
        } else if (e.key === 'Escape') {
            navigate('/gallery');
        }
    }, [navigateRelative, navigate]);

    return {
        currentIndex,
        hasPrevious: currentIndex > 0,
        hasNext: currentIndex < artworks.length - 1,
        totalCount: artworks.length,
        navigateToArtwork,
        navigateRelative,
        handleKeyDown,
    };
};